import { useEffect, useCallback, memo, type ReactNode, type CSSProperties } from 'react';
import Switch from 'react-switch';
import cn from 'classnames';
import isNil from 'lodash/isNil';
import { areEqualFields } from 'utils/form';
import theme from 'config/theme';
import type { TFieldComponentProps } from './types';
import styles from './Switch.module.scss';

export interface ISwitchInputProps extends TFieldComponentProps<boolean | null | undefined> {
  /** Replaces the formik update. */
  onChange?: (checked: boolean) => void;
  label?: ReactNode;
  style?: CSSProperties;
}

const SwitchInput = ({
  field,
  form: { touched, errors, setFieldValue },
  onChange,
  ...props
}: ISwitchInputProps) => {
  const { label, style } = props;
  const { name, value } = field;

  const handleChange = useCallback(
    (v: boolean) => {
      setFieldValue(name, v);
    },
    [name, setFieldValue],
  );

  const hasError = touched[name] && errors[name];

  useEffect(() => {
    if (isNil(value)) setFieldValue(name, false);
  }, [name, setFieldValue, value]);

  return (
    <div className={cn('field-wrapper', styles.switchWrapper)} style={style}>
      <Switch
        // The input renders the attribute as the text "true"/"false" either way.
        value={String(value || false)}
        name={name}
        onChange={onChange || handleChange}
        checked={value || false}
        offColor={theme.whiteSmokeColor}
        onColor={theme.primaryColor}
        offHandleColor="#808080"
        height={20}
        width={40}
        uncheckedIcon={false}
        checkedIcon={false}
        className={cn({ 'error-input': hasError }, styles.switch)}
      />
      <label className={styles.label} htmlFor={name}>
        {label}
      </label>
      {hasError && <div className="error-text">{errors[name]}</div>}
    </div>
  );
};

export default memo(SwitchInput, areEqualFields);
